import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Clock, Gift, AlertTriangle, ArrowRight, Sparkles, Zap, X } from 'lucide-react';
import { Button } from '../ui/button';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Trial Status Banner
 * 
 * Displays in the self-serve dashboard to show:
 * - Days remaining in trial
 * - Upgrade CTA
 * - What happens after trial ends
 * 
 * Urgency levels:
 * - low: 8+ days remaining
 * - medium: 4-7 days remaining  
 * - high: 1-3 days remaining
 * - critical: 0 days (expires today)
 */
const TrialStatusBanner = ({ onDismiss }) => {
  const { token } = useAuth();
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchTrialStatus = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/selfserve/trial/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTrialStatus(response.data);
      } catch (err) {
        console.error('Failed to fetch trial status:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTrialStatus();
  }, [token]);

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  // Don't show if loading, dismissed, or no trial
  if (loading || dismissed || !trialStatus?.has_trial) {
    return null;
  }

  // Don't show if user is on paid plan
  if (trialStatus.is_paid) {
    return null;
  }

  const { days_remaining, status_message, urgency, expiration_action } = trialStatus;

  // Color schemes based on urgency
  const urgencyStyles = {
    low: {
      bg: 'bg-gradient-to-r from-emerald-50 to-emerald-100/50',
      border: 'border-emerald-200',
      icon: 'text-emerald-600',
      iconBg: 'bg-emerald-100',
      text: 'text-emerald-800',
      textSecondary: 'text-emerald-600',
      buttonBg: 'bg-emerald-600 hover:bg-emerald-700',
    },
    medium: {
      bg: 'bg-gradient-to-r from-amber-50 to-amber-100/50',
      border: 'border-amber-200',
      icon: 'text-amber-600',
      iconBg: 'bg-amber-100',
      text: 'text-amber-800',
      textSecondary: 'text-amber-600',
      buttonBg: 'bg-amber-600 hover:bg-amber-700',
    },
    high: {
      bg: 'bg-gradient-to-r from-orange-50 to-orange-100/50',
      border: 'border-orange-200',
      icon: 'text-orange-600',
      iconBg: 'bg-orange-100',
      text: 'text-orange-800',
      textSecondary: 'text-orange-600',
      buttonBg: 'bg-orange-600 hover:bg-orange-700',
    },
    critical: {
      bg: 'bg-gradient-to-r from-red-50 to-red-100/50',
      border: 'border-red-200',
      icon: 'text-red-600',
      iconBg: 'bg-red-100',
      text: 'text-red-800',
      textSecondary: 'text-red-600',
      buttonBg: 'bg-red-600 hover:bg-red-700',
    },
  };

  const styles = urgencyStyles[urgency] || urgencyStyles.low;
  const IconComponent = urgency === 'critical' || urgency === 'high' ? AlertTriangle : (urgency === 'medium' ? Clock : Gift);

  return (
    <div 
      className={`rounded-2xl border ${styles.border} ${styles.bg} p-5 relative overflow-hidden`}
      data-testid="trial-status-banner"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 opacity-10 pointer-events-none">
        <Sparkles className="w-full h-full" />
      </div>
      
      <div className="relative flex items-center justify-between gap-4 flex-wrap">
        {/* Left: Icon + Status */}
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl ${styles.iconBg} flex items-center justify-center flex-shrink-0`}>
            <IconComponent className={styles.icon} size={24} />
          </div>
          <div>
            <p className={`font-bold text-lg ${styles.text}`} data-testid="trial-days-remaining">
              {status_message}
            </p>
            <p className={`text-sm ${styles.textSecondary}`}>
              {days_remaining > 0 
                ? `Upgrade now to keep all your progress and unlock premium features`
                : 'Upgrade to continue using all features'}
            </p>
          </div>
        </div>
        
        {/* Right: CTA + Dismiss */}
        <div className="flex items-center gap-3">
          <Link to="/billing/upgrade">
            <Button 
              className={`${styles.buttonBg} text-white shadow-lg`}
              data-testid="trial-upgrade-btn"
            >
              <Zap size={16} className="mr-2" />
              Upgrade Now
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
          
          {urgency === 'low' && (
            <button
              onClick={handleDismiss}
              className="p-2 rounded-lg hover:bg-black/5 transition-colors"
              aria-label="Dismiss"
            >
              <X size={18} className={styles.textSecondary} />
            </button>
          )}
        </div>
      </div>
      
      {/* Expiration Info (for high/critical urgency) */}
      {(urgency === 'high' || urgency === 'critical') && expiration_action && (
        <div className={`mt-3 pt-3 border-t ${styles.border}`}>
          <p className={`text-xs ${styles.textSecondary}`}>
            <strong>What happens next:</strong> {expiration_action}
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Compact Trial Badge
 * For use in headers or sidebars
 */
export const TrialBadge = ({ className = '' }) => {
  const { token } = useAuth();
  const [trialStatus, setTrialStatus] = useState(null);

  useEffect(() => {
    const fetchTrialStatus = async () => {
      if (!token) return;
      
      try {
        const response = await axios.get(`${API}/selfserve/trial/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTrialStatus(response.data);
      } catch (err) {
        // Silently fail for badge
      }
    };

    fetchTrialStatus();
  }, [token]);

  if (!trialStatus?.has_trial || trialStatus.is_paid) {
    return null;
  }

  const { days_remaining, urgency } = trialStatus;

  const badgeColors = {
    low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    medium: 'bg-amber-100 text-amber-700 border-amber-200',
    high: 'bg-orange-100 text-orange-700 border-orange-200',
    critical: 'bg-red-100 text-red-700 border-red-200',
  };

  return (
    <Link 
      to="/billing/upgrade"
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${badgeColors[urgency] || badgeColors.low} hover:opacity-80 transition-opacity ${className}`}
      data-testid="trial-badge"
    >
      <Clock size={12} />
      <span>{days_remaining} {days_remaining === 1 ? 'day' : 'days'} left</span>
    </Link>
  );
};

export default TrialStatusBanner;
